import { ApolloClient, InMemoryCache, HttpLink, ApolloLink, concat } from "@apollo/client";
import type { FetchResult } from "@apollo/client";
import { Observable } from "@apollo/client/utilities";
import type { Subscriber } from "rxjs";

interface ApolloOperationError {
  message?: string;
  networkError?: unknown;
  graphQLErrors?: unknown;
}

const httpLink = new HttpLink({
  uri: `${process.env.NEXT_PUBLIC_SUPABASE_URL}/graphql/v1`,
});

// Link to ensure proper JSON serialization for JSONB fields
const jsonSerializationLink = new ApolloLink((operation, forward) => {
  // Deep clone and re-serialize variables to ensure proper JSON handling
  if (operation.variables) {
    operation.variables = JSON.parse(JSON.stringify(operation.variables));
  }
  return forward(operation);
});

/**
 * Minimal structural view of the Supabase auth surface this module consumes.
 * The shared client is stored on an untyped `window` global, so we describe
 * exactly the two methods we call rather than pulling in the full client type.
 */
interface SupabaseAuthClientLike {
  auth: {
    getSession: () => Promise<{
      data: { session: { access_token?: string | null } | null };
      error: { message?: string } | null;
    }>;
    onAuthStateChange: (
      callback: (
        event: string,
        session: { access_token?: string | null } | null
      ) => void
    ) => unknown;
  };
}

interface SupabaseClientWindow extends Window {
  __SUPABASE_CLIENT__?: SupabaseAuthClientLike;
}

// Function to get the shared Supabase client instance
const getSupabaseClient = (): SupabaseAuthClientLike | null => {
  if (typeof window === "undefined") return null;

  // Try to get the shared Supabase client from the global context
  const shared = (window as SupabaseClientWindow).__SUPABASE_CLIENT__;
  if (shared) {
    return shared;
  }

  // Fallback to creating a new instance if shared one is not available
  const { createClient } = require("@supabase/supabase-js");
  return createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
  ) as SupabaseAuthClientLike;
};

/**
 * In-memory cache of the current Supabase access token.
 *
 * Previously `authLink` awaited `supabase.auth.getSession()` on EVERY GraphQL
 * operation — a per-request cost every CRM list query paid on the hot path. We
 * now seed the token once (lazily, on the first op) and keep it current via
 * `onAuthStateChange` (fires on INITIAL_SESSION, TOKEN_REFRESHED and
 * SIGNED_OUT). After the first op the token is read synchronously with no
 * session lookup. The token lives only in memory — nothing new is persisted —
 * mirroring the existing `tokenRef` in `client-providers.tsx`.
 */
let cachedAccessToken: string | null = null;
let authListenerAttached = false;

const attachAuthListener = (supabase: SupabaseAuthClientLike): void => {
  if (authListenerAttached) return;
  authListenerAttached = true;
  // The callback fires immediately with the current session (INITIAL_SESSION),
  // then on every refresh/sign-in/sign-out, keeping `cachedAccessToken` honest.
  supabase.auth.onAuthStateChange((_event, session) => {
    cachedAccessToken = session?.access_token ?? null;
  });
};

const authLink = new ApolloLink((operation, forward) => {
  return new Observable<FetchResult>((observer: Subscriber<FetchResult>) => {
    const handleAuth = async () => {
      try {
        // Only try to get auth token on client side
        let token: string | null = null;
        if (typeof window !== "undefined") {
          try {
            const supabase = getSupabaseClient();
            if (supabase) {
              attachAuthListener(supabase);
              // Fast path: use the token kept current by onAuthStateChange.
              token = cachedAccessToken;

              // Cold path: the listener hasn't seeded a token yet (first op
              // right after load). Seed it once via getSession so this request
              // still carries auth; subsequent ops hit the fast path.
              if (!token) {
                const { data, error } = await supabase.auth.getSession();
                if (error) {
                  console.error("GraphQL Auth Error:", error);
                }
                token = data.session?.access_token ?? null;
                cachedAccessToken = token;

                // Debug logging for authentication (cold path only)
                if (process.env.NODE_ENV === "development") {
                  console.log("GraphQL Auth Debug:", {
                    hasToken: !!token,
                    tokenPreview: token ? `${token.substring(0, 20)}...` : "No token",
                    operationName: operation.operationName,
                    error: error?.message,
                    sessionExists: !!data.session,
                  });
                }
              }
            } else {
              console.warn("GraphQL Auth: No Supabase client available");
            }
          } catch (authError) {
            console.error("Failed to get auth session:", authError);
          }
        }

        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }));

        const sub = forward(operation).subscribe({
          next: (result: FetchResult) => observer.next(result),
          error: (error: ApolloOperationError) => {
            // Enhanced error logging for GraphQL authentication issues
            if (process.env.NODE_ENV === "development") {
              console.error("GraphQL Operation Error:", {
                operationName: operation.operationName,
                error: error.message,
                hasToken: !!token,
                networkError: error.networkError,
                graphQLErrors: error.graphQLErrors,
              });
            }
            observer.error(error);
          },
          complete: () => observer.complete(),
        });

        return () => sub.unsubscribe();
      } catch (error) {
        console.error("GraphQL Auth Link Error:", error);
        observer.error(error);
      }
    };

    void handleAuth();
  });
});

export const apolloClient = new ApolloClient({
  link: concat(jsonSerializationLink, concat(authLink, httpLink)),
  // Normalized-store growth is reclaimed by `ApolloCacheGc` (apollo-cache-gc.tsx),
  // which calls `cache.gc()` on route change + idle to drop entries from views
  // the user navigated away from. That is the memory win — it does not change
  // normalization or pagination semantics the CRM list hooks rely on.
  cache: new InMemoryCache(),
  ssrMode: typeof window === "undefined",
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
    },
    query: {
      errorPolicy: "all",
    },
  },
});
